import { router } from '@inertiajs/react';
import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LayoutDashboard, Zap, Settings2, Shield } from "lucide-react";
import { StrategyCard } from '@/components/StrategyCard';
import { Header } from '@/components/Header';
import { SimpleSettings } from '@/components/SimpleSettings';
import { DetailedSettings } from '@/components/DetailedSettings';
import { BotConfirmation } from '@/components/BotConfirmation';
import { ActiveBots } from '@/components/ActiveBots';
import { BotDetail } from '@/components/BotDetail';
import { HistoryDashboard } from '@/components/HistoryDashboard';
import { DailyStats } from '@/components/DailyStats';
import { BottomNav, DesktopNav } from '@/components/BottomNav';
import { ProfilePanel } from '@/components/dca/ProfilePanel';
import { STRATEGIES } from '@/constants/strategies';
import { DcaRound, PositionType } from '@/types';
import { Card, CardHeader, CardContent, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, X } from "lucide-react";
import { Theme } from '@/components/ThemeToggle';
import { Provider } from 'react-redux';
import { store } from '@/store';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { setTheme, setIsPrivate, toggleIsPrivate } from '@/store/slices/uiSlice';
import { getDefaultSymbol } from '@/components/dca/ExchangeUIConfigs';


// ─── Hash URL 파서 ─────────────────────────────────────────────────────────────
// #bots        → { tab: 'bots', botId: null }
// #bots/42     → { tab: 'bots', botId: '42' }
// #history     → { tab: 'history', botId: null }
// #setup       → { tab: 'recommend', botId: null }  (전략 설정)
// (없음)       → { tab: 'bots', botId: null }
const SETUP_TABS = new Set(['recommend', 'simple', 'detail']);

function parseHash(hash: string): { tab: string; botId: string | null } {
  const raw = hash.replace(/^#\/?/, '');
  if (!raw) return { tab: 'bots', botId: null };
  const parts = raw.split('/');
  // #setup → 전략 설정 탭(recommend)으로 매핑
  const rawTab = parts[0] || 'bots';
  if (rawTab === 'profile') return { tab: 'profile', botId: null };
  const tab = rawTab === 'setup' ? 'recommend' : rawTab;
  const botId = parts[1] || null;
  return { tab, botId };
}

function buildHash(tab: string, botId: string | null = null): string {
  // 전략 설정 관련 탭은 모두 #setup으로 표시
  const hashTab = SETUP_TABS.has(tab) ? 'setup' : tab;
  if (botId) return `#${hashTab}/${botId}`;
  return `#${hashTab}`;
}

export default function App(props: any) {
  return (
    <Provider store={store}>
      <AppContent {...props} />
    </Provider>
  );
}

function AppContent({ initial_bots = [], flash, current_user, is_testnet, dca_enabled, disable_all_testnet, enabled_exchanges, testnet_min_notional, engine_stats, auto_scanner_enabled, auto_scanner_max_active_sessions, auto_scanner_exchange, auto_scanner_target_symbols = [] }: { initial_bots?: any[], flash?: any, current_user: { name?: string; email: string; logout_path: string; is_active: boolean; is_admin: boolean; is_impersonated?: boolean; max_bots: number; current_bot_count: number; profile: { binance_api_key?: string; binance_api_secret?: string; binance_testnet_api_key?: string; binance_testnet_api_secret?: string; telegram_bot_token?: string; telegram_chat_id?: string; } }, is_testnet?: boolean, dca_enabled?: boolean, disable_all_testnet?: boolean, enabled_exchanges?: Record<string, boolean>, testnet_min_notional?: number, engine_stats?: { total_capacity: number, busy_count: number, enqueued_count: number }, auto_scanner_enabled?: boolean, auto_scanner_max_active_sessions?: any, auto_scanner_exchange?: string, auto_scanner_target_symbols?: string[] }) {
  // ── hash 기반 초기 상태 ──
  const init = parseHash(window.location.hash);
  const [activeTab, setActiveTabState] = useState(init.tab);
  const [selectedBotId, setSelectedBotIdState] = useState<string | null>(init.botId);
  const [showNotification, setShowNotification] = useState(!!(flash?.notice || flash?.alert));

  // (Moved to consolidated polling loop below)

  useEffect(() => {
    if (flash?.notice || flash?.alert) {
      setShowNotification(true);
      const timer = setTimeout(() => setShowNotification(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [flash]);

  // ── 탭 / 봇 선택 → URL hash 업데이트 ──
  const setActiveTab = useCallback((tab: string) => {
    setActiveTabState(tab);
    setSelectedBotIdState(null);
    window.history.pushState(null, '', buildHash(tab));
  }, []);

  const setSelectedBotId = useCallback((id: string | null) => {
    setSelectedBotIdState(id);
    if (id) {
      setActiveTabState('bots');
      window.history.pushState(null, '', buildHash('bots', id));
    } else {
      window.history.pushState(null, '', buildHash('bots'));
      // 상세 화면에서 목록으로 복귀 시 즉시 최신 데이터로 리프레시
      router.reload({ only: ['initial_bots'], preserveUrl: true });
    }
  }, []);

  // ── 브라우저 뒤로가기 / 앞으로가기 처리 ──
  useEffect(() => {
    const onPopState = () => {
      const { tab, botId } = parseHash(window.location.hash);
      setActiveTabState(tab);
      setSelectedBotIdState(botId);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // ── URL Hash와 상태 동기화 (URL이 소실되어도 복구) ──
  useEffect(() => {
    const currentHash = window.location.hash;
    const expectedHash = buildHash(activeTab, selectedBotId);
    if (currentHash !== expectedHash) {
      // pushState가 아닌 replaceState를 사용하여 히스토리 스택 중복 방지
      window.history.replaceState(null, '', expectedHash);
    }
  }, [activeTab, selectedBotId]);


  // ── 폼 상태 ──
  const [leverage, setLeverage] = useState(20);
  const [positionType, setPositionType] = useState<PositionType>('LONG');
  const [entryCount, setEntryCount] = useState(7);
  const [entryMultiplier, setEntryMultiplier] = useState(2);
  const [entryInterval, setEntryInterval] = useState(0.75);
  const [takeProfit, setTakeProfit] = useState(20);
  const [selectedSymbol, setSelectedSymbol] = useState('BTCUSDT');
  const [repeatExecution, setRepeatExecution] = useState(true);
  const [repeatType, setRepeatType] = useState('market');
  const [stopLoss, setStopLoss] = useState(false);
  const [stopLossValue, setStopLossValue] = useState(58);
  const [stopBotOnSL, setStopBotOnSL] = useState(false);
  const [useMaFilter, setUseMaFilter] = useState(false);
  const [useMa5Filter, setUseMa5Filter] = useState(false);
  const [useRsiEntry, setUseRsiEntry] = useState(false);
  const [useRsiScaling, setUseRsiScaling] = useState(false);
  const [useTsEntry, setUseTsEntry] = useState(false);
  const [useTsScaling, setUseTsScaling] = useState(false);
  const [maPeriod, setMaPeriod] = useState(25);
  const [rsiThreshold, setRsiThreshold] = useState(30);
  const [botName, setBotName] = useState('New DCA Bot');
  const [baseQty, setBaseQty] = useState(1);
  const [entryType, setEntryType] = useState<'percent' | 'fixed'>('fixed');
  const [entryAmount, setEntryAmount] = useState(10);
  const [balance, setBalance] = useState<number>(0);
  const [useAllBalance, setUseAllBalance] = useState(true);
  const [feePct, setFeePct] = useState(0.04);
  const [limitFeePct, setLimitFeePct] = useState(0.02);
  const [candleInterval, setCandleInterval] = useState('5m');
  const [isAutoMargin, setIsAutoMargin] = useState(true);
  const [principalAmount, setPrincipalAmount] = useState(1000);
  const [marginMode, setMarginMode] = useState<'crossed' | 'isolated' | 'cross'>('crossed');
  const [isEntryFixed, setIsEntryFixed] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [slStrategy, setSlStrategy] = useState<'realtime' | 'candle_close'>('candle_close');
  const [tpThresholdStep, setTpThresholdStep] = useState<number>(0);
  const [tpThresholdPct, setTpThresholdPct] = useState<number>(0);
  const [useLimitTp, setUseLimitTp] = useState(false);
  const [pullbackPct, setPullbackPct] = useState(0.1);
  const [suspendedSides, setSuspendedSides] = useState<string[]>([]);
  const [exchange, setExchange] = useState('binance');
  const [useTestnet, setUseTestnet] = useState(false);
  const [useProfitOffsetting, setUseProfitOffsetting] = useState(true);
  const [profitOffsetPct, setProfitOffsetPct] = useState(50);
  const [immediateReentry, setImmediateReentry] = useState(false);
  const [use1hTrendFilter, setUse1hTrendFilter] = useState(false);
  const [use1hTrendSl, setUse1hTrendSl] = useState(false);
  const [use1hTrendBe, setUse1hTrendBe] = useState(false);
  const [use1hSidewaysFilter, setUse1hSidewaysFilter] = useState(false);
  const [use1hSidewaysScalingFilter, setUse1hSidewaysScalingFilter] = useState(false);
  const [adxThreshold, setAdxThreshold] = useState(22);
  const [useSuperMaFilter, setUseSuperMaFilter] = useState(false);
  const [useSuperMaBe, setUseSuperMaBe] = useState(true);
  const [useSuperMaScaling, setUseSuperMaScaling] = useState(true);
  const [useSuperMaSlowMode, setUseSuperMaSlowMode] = useState(true);
  const [superMaScoreThreshold, setSuperMaScoreThreshold] = useState(3);
  const [useStochAlignmentEntry, setUseStochAlignmentEntry] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [telegramEnabled, setTelegramEnabled] = useState<boolean | null>(null);
  const [telegramNotifyTypes, setTelegramNotifyTypes] = useState<string[]>([
    'entry', 'scaling', 'tp', 'sl', 'liquidate', 'error', 'info'
  ]);

  const handleExchangeChange = (val: string) => {
    setExchange(val);
    if (val !== 'binance' && val !== 'okx' && val !== 'bybit') {
      setUseTestnet(false);
    }
    setSelectedSymbol(getDefaultSymbol(val));
  };

  const handleIsLiveChange = (val: boolean) => {
    setIsLive(val);
    if (!val) {
      setUseTestnet(false);
    }
  };

  // ── 실시간 폴링 (봇 상세가 닫혀있고 설정창이 아닐 때만 전체 목록 갱신) ──
  useEffect(() => {
    if (activeTab !== 'bots' || showConfirmation) return;

    // 상세 화면이 열려있어도 전체 봇 데이터는 5초마다 계속 갱신하여 동기화 유지
    const interval = setInterval(() => {
      router.reload({
        only: ['initial_bots', 'engine_stats'],
        preserveUrl: true
      });
    }, 10000);
    return () => clearInterval(interval);
  }, [activeTab, showConfirmation]);

  // ─── 리덕스 상태 관리 ──────────────────────────────────────────────────
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.ui.theme);
  const isPrivate = useAppSelector((state) => state.ui.isPrivate);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('dark', 'light-simple');
    if (theme !== 'default') {
      root.classList.add(theme);
    }
  }, [theme]);

  const [dcaRounds, setDcaRounds] = useState<DcaRound[]>([
    { id: 1, qty: 10, distance: 0.75 },
    { id: 2, qty: 20, distance: 1.5 },
    { id: 3, qty: 40, distance: 2.25 },
    { id: 4, qty: 80, distance: 3.0 }
  ]);

  // ─── 임시 저장 (Draft) 기능 ───────────────────────────────────────────────
  const DRAFT_KEY = 'dca_bot_draft_v1';
  const [hasDraft, setHasDraft] = useState(false);

  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (draft) {
      setHasDraft(true);
    }
  }, []);

  const saveDraft = () => {
    const draftData = {
      leverage, positionType, entryCount, entryMultiplier, entryInterval,
      takeProfit, selectedSymbol, repeatExecution, repeatType, stopLoss,
      stopLossValue, stopBotOnSL, useMaFilter, useMa5Filter, useRsiEntry, useRsiScaling,
      useTsEntry, useTsScaling,
      maPeriod, rsiThreshold,
      botName,
      baseQty, entryType, entryAmount, dcaRounds, feePct, limitFeePct, balance, useAllBalance, candleInterval,
      useLimitTp, pullbackPct, exchange, suspendedSides, useTestnet,
      useProfitOffsetting, profitOffsetPct, principalAmount, marginMode, isAutoMargin, isEntryFixed,
      immediateReentry, use1hSidewaysFilter, use1hSidewaysScalingFilter, adxThreshold,
      useSuperMaFilter, superMaScoreThreshold, useStochAlignmentEntry,
      useSuperMaBe, useSuperMaScaling, useSuperMaSlowMode,
      telegramEnabled, telegramNotifyTypes
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
    setHasDraft(true);
    setShowNotification(true);
    // 임시로 flash 알림처럼 표시 (notice 대체)
    alert('현재 설정이 브라우저에 임시 저장되었습니다.');
  };

  const loadDraft = () => {
    const draftStr = localStorage.getItem(DRAFT_KEY);
    if (!draftStr) return;
    try {
      const d = JSON.parse(draftStr);
      if (d.leverage !== undefined) setLeverage(d.leverage);
      if (d.positionType) setPositionType(d.positionType);
      if (d.entryCount !== undefined) setEntryCount(d.entryCount);
      if (d.entryMultiplier !== undefined) setEntryMultiplier(d.entryMultiplier);
      if (d.entryInterval !== undefined) setEntryInterval(d.entryInterval);
      if (d.takeProfit !== undefined) setTakeProfit(d.takeProfit);
      if (d.selectedSymbol) setSelectedSymbol(d.selectedSymbol);
      if (d.repeatExecution !== undefined) setRepeatExecution(d.repeatExecution);
      if (d.repeatType) setRepeatType(d.repeatType);
      if (d.stopLoss !== undefined) setStopLoss(d.stopLoss);
      if (d.stopLossValue !== undefined) setStopLossValue(d.stopLossValue);
      if (d.stopBotOnSL !== undefined) setStopBotOnSL(d.stopBotOnSL);
      if (d.useMaFilter !== undefined) setUseMaFilter(d.useMaFilter);
      if (d.useMa5Filter !== undefined) setUseMa5Filter(d.useMa5Filter);
      if (d.useRsiEntry !== undefined) setUseRsiEntry(d.useRsiEntry);
      if (d.useRsiScaling !== undefined) setUseRsiScaling(d.useRsiScaling);
      if (d.useTsEntry !== undefined) setUseTsEntry(d.useTsEntry);
      if (d.useTsScaling !== undefined) setUseTsScaling(d.useTsScaling);
      if (d.maPeriod !== undefined) setMaPeriod(d.maPeriod);
      if (d.rsiThreshold !== undefined) setRsiThreshold(d.rsiThreshold);
      if (d.botName) setBotName(d.botName);
      if (d.baseQty !== undefined) setBaseQty(d.baseQty);
      if (d.feePct !== undefined) setFeePct(d.feePct);
      if (d.limitFeePct !== undefined) setLimitFeePct(d.limitFeePct);
      if (d.balance !== undefined) setBalance(d.balance);
      if (d.useAllBalance !== undefined) setUseAllBalance(d.useAllBalance);
      if (d.candleInterval) setCandleInterval(d.candleInterval);
      if (d.dcaRounds) setDcaRounds(d.dcaRounds);
      if (d.entryType) setEntryType(d.entryType);
      if (d.entryAmount !== undefined) setEntryAmount(d.entryAmount);
      if (d.isAutoMargin !== undefined) setIsAutoMargin(d.isAutoMargin);
      if (d.principalAmount !== undefined) setPrincipalAmount(Number(d.principalAmount));
      if (d.marginMode) setMarginMode(d.marginMode.toLowerCase() as 'crossed' | 'isolated');
      if (d.isEntryFixed !== undefined) setIsEntryFixed(d.isEntryFixed);
      if (d.isLive !== undefined) handleIsLiveChange(d.isLive);
      if (d.tpThresholdStep !== undefined) setTpThresholdStep(d.tpThresholdStep);
      if (d.tpThresholdPct !== undefined) setTpThresholdPct(d.tpThresholdPct);
      if (d.useLimitTp !== undefined) setUseLimitTp(d.useLimitTp);
      if (d.pullbackPct !== undefined) setPullbackPct(d.pullbackPct);
      if (d.exchange) setExchange(d.exchange);
      if (d.suspendedSides) setSuspendedSides(d.suspendedSides);
      if (d.useTestnet !== undefined) setUseTestnet(d.useTestnet);
      if (d.useProfitOffsetting !== undefined) setUseProfitOffsetting(d.useProfitOffsetting);
      if (d.profitOffsetPct !== undefined) setProfitOffsetPct(d.profitOffsetPct);
      if (d.immediateReentry !== undefined) setImmediateReentry(d.immediateReentry);
      if (d.use1hSidewaysFilter !== undefined) setUse1hSidewaysFilter(d.use1hSidewaysFilter);
      if (d.use1hSidewaysScalingFilter !== undefined) setUse1hSidewaysScalingFilter(d.use1hSidewaysScalingFilter);
      if (d.adxThreshold !== undefined) setAdxThreshold(d.adxThreshold);
      if (d.useSuperMaFilter !== undefined) setUseSuperMaFilter(d.useSuperMaFilter);
      if (d.useSuperMaBe !== undefined) setUseSuperMaBe(d.useSuperMaBe);
      if (d.useSuperMaScaling !== undefined) setUseSuperMaScaling(d.useSuperMaScaling);
      if (d.useSuperMaSlowMode !== undefined) setUseSuperMaSlowMode(d.useSuperMaSlowMode);
      if (d.superMaScoreThreshold !== undefined) setSuperMaScoreThreshold(d.superMaScoreThreshold);
      if (d.useStochAlignmentEntry !== undefined) setUseStochAlignmentEntry(d.useStochAlignmentEntry);
      if (d.telegramEnabled !== undefined) setTelegramEnabled(d.telegramEnabled);
      if (d.telegramNotifyTypes) setTelegramNotifyTypes(d.telegramNotifyTypes);

      setHasDraft(false); // 불러온 후에는 안내 숨김
      alert('저장된 설정을 불러왔습니다.');
    } catch (e) {
      console.error("Failed to load draft", e);
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
  };

  const handleCloneBot = (sourceBot: any) => {
    const settings = sourceBot.settings || {};
    const getVal = (key: string, defaultVal: any) => {
      if (sourceBot[key] !== undefined && sourceBot[key] !== null) return sourceBot[key];
      if (settings[key] !== undefined && settings[key] !== null) return settings[key];
      return defaultVal;
    };

    setBotName(`${getVal('name', '')} (복사본)`);
    setSelectedSymbol(getVal('symbol', 'BTCUSDT'));
    const side = getVal('side', 'long');
    setPositionType(side.toUpperCase() as PositionType);
    setBaseQty(getVal('base_qty', 1));
    setEntryMultiplier(getVal('multiplier', 2));
    setEntryInterval(getVal('step_pct', 0.75));
    
    const scalingModeVal = getVal('scaling_mode', 'auto');
    const isAuto = scalingModeVal === 'auto';
    setIsAutoMargin(isAuto);

    const maxScaling = getVal('max_scaling', 7);
    setEntryCount(maxScaling);
    setTakeProfit(getVal('tp_pct', 20));
    
    const slPct = getVal('sl_pct', 0);
    setStopLoss(slPct > 0);
    setStopLossValue(slPct > 0 ? slPct : 58);
    setLeverage(getVal('leverage', 20));
    setRepeatExecution(getVal('cyclic', true));
    setStopBotOnSL(getVal('stop_bot_on_sl', false));
    setUseMaFilter(getVal('use_ma_filter', false));
    setUseMa5Filter(getVal('use_ma5_filter', false) || settings['use_ma5_filter'] || false);
    setUseRsiEntry(getVal('use_rsi_entry', false) || settings['use_rsi_entry'] || false);
    setUseRsiScaling(getVal('use_rsi_scaling', false) || settings['use_rsi_scaling'] || false);
    setUseTsEntry(getVal('use_ts_entry', false) || settings['use_ts_entry'] || false);
    setUseTsScaling(getVal('use_ts_scaling', false) || settings['use_ts_scaling'] || false);
    setMaPeriod(getVal('ma_period', 25));
    setRsiThreshold(getVal('rsi_threshold', 30));
    
    const entryTypeVal = getVal('entry_type', 'fixed');
    setEntryType(entryTypeVal);
    
    const entryAmountVal = getVal('entry_amount', 10);
    setEntryAmount(entryAmountVal);
    
    setBalance(getVal('balance', 0));
    setUseAllBalance(!getVal('use_allocated_balance', true));
    setFeePct(getVal('fee_pct', 0.04));
    setLimitFeePct(getVal('limit_fee_pct', 0.02));
    setCandleInterval(getVal('interval', '5m'));
    
    const principalAmountVal = Number(getVal('principal_amount', 1000));
    setPrincipalAmount(principalAmountVal);
    
    setMarginMode(getVal('margin_mode', 'crossed'));
    setSlStrategy(getVal('sl_strategy', 'candle_close'));
    setTpThresholdStep(getVal('tp_threshold_step', 0));
    setTpThresholdPct(getVal('tp_threshold_pct', 0));
    setUseLimitTp(getVal('use_limit_tp', false) || settings['use_limit_tp'] || false);
    setPullbackPct(getVal('pullback_pct', 0.1));
    setSuspendedSides(getVal('suspended_sides', []));
    setExchange(getVal('exchange', 'binance'));
    setUseTestnet(getVal('use_testnet', false));
    setUseProfitOffsetting(getVal('use_profit_offsetting', true) || settings['use_profit_offsetting'] || false);
    setProfitOffsetPct(getVal('profit_offset_pct', 50));
    setImmediateReentry(getVal('immediate_reentry', false) || settings['immediate_reentry'] || false);
    setUse1hTrendFilter(getVal('use_1h_trend_filter', false) || settings['use_1h_trend_filter'] || false);
    setUse1hTrendSl(getVal('use_1h_trend_sl', false) || settings['use_1h_trend_sl'] || false);
    setUse1hTrendBe(getVal('use_1h_trend_be', false) || settings['use_1h_trend_be'] || false);
    setUse1hSidewaysFilter(getVal('use_1h_sideways_filter', false) || settings['use_1h_sideways_filter'] || false);
    setUse1hSidewaysScalingFilter(getVal('use_1h_sideways_scaling_filter', false) || settings['use_1h_sideways_scaling_filter'] || false);
    setAdxThreshold(getVal('adx_threshold', 22));
    setUseSuperMaFilter(getVal('use_super_ma_filter', false) || settings['use_super_ma_filter'] || false);
    setUseSuperMaBe(getVal('use_super_ma_be', true) || settings['use_super_ma_be'] || false);
    setUseSuperMaScaling(getVal('use_super_ma_scaling', true) || settings['use_super_ma_scaling'] || false);
    setUseSuperMaSlowMode(getVal('use_super_ma_slow_mode', true) || settings['use_super_ma_slow_mode'] || false);
    setSuperMaScoreThreshold(getVal('super_ma_score_threshold', 3));
    setUseStochAlignmentEntry(getVal('use_stoch_alignment_entry', false) || settings['use_stoch_alignment_entry'] || false);
    setTelegramEnabled(getVal('telegram_enabled', null));
    setTelegramNotifyTypes(getVal('telegram_notify_types', ['entry', 'scaling', 'tp', 'sl', 'liquidate', 'error', 'info']));
    
    const customTargets = settings['custom_targets'] || sourceBot.custom_targets || [];
    if (customTargets.length > 0) {
      setDcaRounds(customTargets.map((ct: any) => ({
        id: ct.step || ct.step_number || 1,
        qty: ct.cost || ct.target_qty || 10,
        distance: ct.distance_pct || ct.target_price_pct || 0.75
      })));
    }

    setSelectedBotId(null);
    setActiveTab('detail');
    alert('설정이 성공적으로 복제되었습니다. 상세 설정 탭에서 커스터마이즈 후 생성하세요.');
  };

  const handleCreateBot = () => {
    const payload = {
      bot: {
        name: botName,
        symbol: selectedSymbol,
        side: positionType.toLowerCase(),
        base_qty: baseQty,
        multiplier: entryMultiplier,
        step_pct: entryInterval,
        scaling_mode: isAutoMargin ? 'auto' : 'custom',
        max_scaling: (activeTab === 'detail' && !isAutoMargin) ? dcaRounds.length : entryCount,
        tp_pct: takeProfit,
        sl_pct: stopLoss ? stopLossValue : 0,
        leverage: leverage,
        cyclic: repeatExecution,
        stop_bot_on_sl: stopBotOnSL,
        use_ma_filter: useMaFilter,
        use_ma5_filter: useMa5Filter,
        use_rsi_entry: useRsiEntry,
        use_rsi_scaling: useRsiScaling,
        use_ts_entry: useTsEntry,
        use_ts_scaling: useTsScaling,
        fee_pct: feePct,
        limit_fee_pct: limitFeePct,
        balance: balance,
        use_allocated_balance: activeTab === 'detail' ? !useAllBalance : false,
        interval: candleInterval,
        entry_type: entryType,
        entry_amount: entryAmount,
        initial_entry_pct: entryType === 'percent' ? entryAmount : null,
        is_auto_margin: isAutoMargin,
        principal_amount: principalAmount,
        margin_mode: marginMode,
        is_live: isLive,
        sl_strategy: slStrategy,
        tp_threshold_step: tpThresholdStep > 0 ? tpThresholdStep : null,
        tp_threshold_pct: tpThresholdPct > 0 ? tpThresholdPct : null,
        use_limit_tp: useLimitTp,
        pullback_pct: pullbackPct,
        exchange: exchange,
        use_testnet: useTestnet,
        use_profit_offsetting: useProfitOffsetting,
        profit_offset_pct: profitOffsetPct,
        immediate_reentry: immediateReentry,
        use_1h_trend_filter: use1hTrendFilter,
        use_1h_trend_sl: use1hTrendSl,
        use_1h_trend_be: use1hTrendBe,
        use_1h_sideways_filter: use1hSidewaysFilter,
        use_1h_sideways_scaling_filter: use1hSidewaysScalingFilter,
        adx_threshold: adxThreshold,
        use_super_ma_filter: useSuperMaFilter,
        use_super_ma_be: useSuperMaBe,
        use_super_ma_scaling: useSuperMaScaling,
        use_super_ma_slow_mode: useSuperMaSlowMode,
        super_ma_score_threshold: superMaScoreThreshold,
        use_stoch_alignment_entry: useStochAlignmentEntry,
        suspended_sides: suspendedSides,
        telegram_enabled: telegramEnabled,
        telegram_notify_types: telegramNotifyTypes,
        custom_targets: dcaRounds.map(r => ({
          step: r.id,
          cost: r.qty,
          distance_pct: r.distance
        })),
        ma_period: maPeriod,
        rsi_threshold: rsiThreshold
      }
    };
    router.post('/dca', payload, {
      onSuccess: () => {
        setShowConfirmation(false);
        setActiveTab('bots');
        clearDraft(); // 생성 성공 시 드래프트 삭제
      }
    });
  };

  const handleSelectStrategy = (strategy: typeof STRATEGIES[0]) => {
    setSelectedSymbol(strategy.symbol);
    setLeverage(strategy.leverage);
    setPositionType(strategy.type as PositionType);
    setEntryCount(strategy.entryCount);
    setEntryMultiplier(strategy.entryMultiplier);
    setEntryInterval(strategy.entryInterval);
    setTakeProfit(strategy.takeProfit);
    setBotName(`${strategy.symbol} ${strategy.title}`);
    setActiveTab('simple');
    const currentBaseAmount = entryType === 'percent' ? (balance > 0 ? balance : principalAmount) * (baseQty / 100) : entryAmount;
    setDcaRounds(Array.from({ length: strategy.entryCount }).map((_, i) => ({
      id: i + 1,
      qty: currentBaseAmount * Math.pow(strategy.entryMultiplier, i),
      distance: i === 0 ? 0 : strategy.entryInterval
    })));
    setIsAutoMargin(true);
  };

  const applyPreset = (type: 'hybrid' | 'martingale' | 'scalping') => {
    if (type === 'hybrid') {
      setLeverage(2);
      setEntryMultiplier(1.5);
      setEntryInterval(2.0);
      setEntryCount(10);
      setTakeProfit(1.5);
      setUseRsiEntry(true);
      setRsiThreshold(35);
      setUseSuperMaFilter(true);
      setSuperMaScoreThreshold(3);
      setUse1hTrendFilter(true);
      setUseStochAlignmentEntry(true);
      setUseLimitTp(true);
      setPullbackPct(0.1);
    } else if (type === 'martingale') {
      setLeverage(5);
      setEntryMultiplier(2.0);
      setEntryInterval(1.0);
      setEntryCount(7);
      setTakeProfit(1.0);
      setUseRsiEntry(false);
      setUseSuperMaFilter(false);
      setUse1hTrendFilter(false);
      setUseStochAlignmentEntry(false);
      setUseLimitTp(false);
    } else if (type === 'scalping') {
      setLeverage(10);
      setEntryMultiplier(1.3);
      setEntryInterval(0.5);
      setEntryCount(15);
      setTakeProfit(0.5);
      setUseRsiEntry(true);
      setRsiThreshold(30);
      setUseSuperMaFilter(true);
      setSuperMaScoreThreshold(2);
      setUse1hTrendFilter(false);
      setUseStochAlignmentEntry(true);
      setUseLimitTp(true);
      setPullbackPct(0.05);
    }
    alert(`${type === 'hybrid' ? '🏆 슈퍼 하이브리드' : type === 'martingale' ? '🔥 클래식 마틴' : '⚡ 정밀 스캘핑'} 프리셋이 적용되었습니다.`);
  };

  const addRound = () => {
    const last = dcaRounds[dcaRounds.length - 1];
    setDcaRounds([...dcaRounds, {
      id: dcaRounds.length + 1,
      qty: last ? last.qty * 2 : 10,
      distance: last ? last.distance + 0.75 : 0.75
    }]);
  };

  const removeRound = (id: number) => {
    if (dcaRounds.length <= 1) return;
    setDcaRounds(dcaRounds.filter(r => r.id !== id).map((r, i) => ({ ...r, id: i + 1 })));
  };

  const updateRound = (id: number, field: 'qty' | 'distance', value: number) => {
    setDcaRounds(dcaRounds.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  // 비율일 때는 총 예산(또는 밸런스) 기준으로 실제 금액($)을 산출하여 계산
  const baseAmount = entryType === 'percent'
    ? (balance > 0 ? balance : principalAmount) * (baseQty / 100)
    : entryAmount;

  const predictedTotalOpenAmount = entryCount > 0
    ? baseAmount * (Math.pow(entryMultiplier, entryCount) - 1) / (entryMultiplier - 1)
    : baseAmount;

  const totalMargin = activeTab === 'detail'
    ? dcaRounds.reduce((a, c) => a + c.qty, 0)
    : predictedTotalOpenAmount;

  // Multiplier 합계 계산 함수 (Principal <-> BaseQty 전환용)
  const getSumMultiplier = useCallback(() => {
    const count = entryCount || 1;
    const mult = entryMultiplier || 1;
    if (mult === 1) return count;
    return (Math.pow(mult, count) - 1) / (mult - 1);
  }, [entryCount, entryMultiplier]);

  // Principal 변경 시 -> BaseQty/EntryAmount 업데이트
  const handlePrincipalAmountChange = (val: number) => {
    const safeVal = isNaN(val) ? 0 : val;
    setPrincipalAmount(safeVal);
    if (!isAutoMargin || isEntryFixed) return;

    const sumM = getSumMultiplier();
    const entryUsdt = safeVal / sumM;

    setEntryAmount(Number(entryUsdt.toFixed(2)));
    if (balance > 0) {
      setBaseQty(Number((entryUsdt / balance * 100).toFixed(2)));
    } else {
      setBaseQty(Number((1 / sumM * 100).toFixed(2))); // Principal 대비 비율로 폴백
    }
  };

  // BaseQty/EntryAmount 변경 시 -> Principal 업데이트
  const handleEntryAmountChange = (val: number) => {
    const safeVal = isNaN(val) ? 0 : val;
    let entryInUsdt = safeVal;
    const sumM = getSumMultiplier();

    if (entryType === 'percent') {
      setBaseQty(safeVal);
      // 이제 무조건 principalAmount(전략 총예산)를 우선 기준으로 삼음
      const refScale = principalAmount > 0 ? principalAmount : (balance > 0 ? balance : 1);
      entryInUsdt = refScale * (safeVal / 100);
      setEntryAmount(Number(entryInUsdt.toFixed(2)));
    } else {
      setEntryAmount(safeVal);
      const refScale = principalAmount > 0 ? principalAmount : (balance > 0 ? balance : 1);
      setBaseQty(Number((safeVal / refScale * 100).toFixed(2)));
    }

    if (!isAutoMargin) return;

    // '1회차 고정(isEntryFixed)'이 켜져 있을 때만 입력된 금액을 기준으로 총 예산을 재설정함
    if (isEntryFixed) {
      setPrincipalAmount(Number((entryInUsdt * sumM).toFixed(2)));
    }
  };

  // 단위 전환 (Percent <-> Fixed) 시 값 변환
  const handleEntryTypeChange = (newType: 'percent' | 'fixed') => {
    if (entryType === newType) return;

    if (newType === 'percent') {
      // 이제 항상 principalAmount를 기준으로 %를 계산함 (지갑 잔고보다 사용자 입력 예산이 더 직관적임)
      const refScale = principalAmount > 0 ? principalAmount : (balance > 0 ? balance : 1);
      const newPercent = (entryAmount / refScale) * 100;
      setBaseQty(Number(newPercent.toFixed(2)));
    } else {
      // % -> $ 전환 시에도 principalAmount 기준 환산
      const refScale = principalAmount > 0 ? principalAmount : (balance > 0 ? balance : 1);
      const newAmount = refScale * (baseQty / 100);
      setEntryAmount(Number(newAmount.toFixed(2)));
    }
    setEntryType(newType);
  };

  // 1. 전략 구조(총 예산, 회차, 배수) 변경 시 "완벽한 비율의 1회차 비용" 재계산 (자동 맞춤)
  useEffect(() => {
    // 사용자가 1회차 고정(Lock) 아이콘을 켰다면 자동 맞춤(Overwriting)을 방지함
    if (!isAutoMargin || isEntryFixed) return;

    const sumM = getSumMultiplier();
    if (sumM <= 0) return;

    // Budget -> Entry Cost 계산 (이제 무조건 principalAmount 기준)
    const currentBase = principalAmount / sumM;
    const roundedBase = Number((currentBase).toFixed(2));

    // 현재 값이 계산된 값과 이미 같으면 업데이트하지 않음 (무한 루프 및 불필요한 이벤트 방지)
    if (Math.abs(entryAmount - roundedBase) > 0.001) {
      setEntryAmount(roundedBase);
    }

    // % 값도 principalAmount 기준으로 갱신하여 0.79% 등 입력한 수치가 유지되도록 함
    const refScale = principalAmount > 0 ? principalAmount : (balance > 0 ? balance : 1);
    const newBaseQty = Number((roundedBase / refScale * 100).toFixed(2));
    if (Math.abs(baseQty - newBaseQty) > 0.001) {
      setBaseQty(newBaseQty);
    }
  }, [isAutoMargin, isEntryFixed, balance, principalAmount, entryCount, entryMultiplier, getSumMultiplier]);

  // 2. 현재 설정된 1회차 비용(사용자 수동 변경 포함)을 기준으로 상세 회차(테이블) 업데이트
  useEffect(() => {
    if (!isAutoMargin) return;

    const baseAmount = entryType === 'percent'
      ? (principalAmount > 0 ? principalAmount : (balance > 0 ? balance : 1)) * (baseQty / 100)
      : entryAmount;

    const newRounds: DcaRound[] = [];
    let currentQty = baseAmount;
    for (let i = 1; i <= entryCount; i++) {
      newRounds.push({
        id: i,
        qty: Number((currentQty).toFixed(2)),
        distance: i === 1 ? 0 : entryInterval
      });
      currentQty *= entryMultiplier;
    }
    setDcaRounds(newRounds);
  }, [isAutoMargin, baseQty, entryAmount, entryType, balance, principalAmount, entryCount, entryMultiplier, entryInterval]);

  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-background font-sans antialiased text-foreground flex items-center justify-center p-4 transition-colors duration-300">
        <BotConfirmation
          symbol={selectedSymbol}
          positionType={positionType}
          leverage={leverage}
          entryCount={activeTab === 'detail' ? dcaRounds.length : entryCount}
          totalMargin={totalMargin}
          takeProfit={takeProfit}
          stopLoss={stopLoss}
          stopLossValue={stopLossValue}
          rounds={activeTab === 'detail' ? dcaRounds : Array.from({ length: entryCount }).map((_, i) => ({
            id: i + 1,
            qty: baseAmount * Math.pow(entryMultiplier, i),
            distance: i === 0 ? 0 : entryInterval
          }))}
          principalAmount={isAutoMargin ? principalAmount : dcaRounds.reduce((acc, r) => acc + r.qty, 0)}
          multiplier={entryMultiplier}
          interval={entryInterval}
          useMaFilter={useMaFilter}
          useRsiEntry={useRsiEntry}
          useRsiScaling={useRsiScaling}
          stopBotOnSL={stopBotOnSL}
          marginMode={marginMode}
          balanceLimit={balance}
          useAllBalance={useAllBalance}
          onBack={() => setShowConfirmation(false)}
          fee_pct={feePct}
          limit_fee_pct={limitFeePct}
          isLive={isLive}
          tpThresholdStep={tpThresholdStep}
          tpThresholdPct={tpThresholdPct}
          useLimitTp={useLimitTp}
          pullbackPct={pullbackPct}
          exchange={exchange}
          useTestnet={useTestnet}
          useProfitOffsetting={useProfitOffsetting}
          profitOffsetPct={profitOffsetPct}
          use1hTrendFilter={use1hTrendFilter}
          use1hTrendSl={use1hTrendSl}
          use1hTrendBe={use1hTrendBe}
          suspendedSides={suspendedSides}
          onConfirm={handleCreateBot}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-sans antialiased text-foreground pb-24 md:pb-8 transition-colors duration-300">
      <div className="max-w-md md:max-w-7xl mx-auto w-full px-4 py-4 md:p-8 space-y-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <Header
            onLogoClick={() => { setActiveTab('bots'); setSelectedBotId(null); }}
          />
          <DesktopNav activeTab={activeTab} setActiveTab={setActiveTab} current_user={current_user} />
        </div>

        {/* 기능 비활성화 경고 */}
        {!dca_enabled && (
          <Alert variant="destructive" className="mb-6 bg-danger/10 border-danger/20 text-danger rounded-2xl shadow-sm">
            <AlertCircle className="h-5 w-5" />
            <AlertTitle className="font-black">DCA 기능 비활성화됨</AlertTitle>
            <AlertDescription className="font-bold text-xs opacity-80">
              현재 시스템 환경에서 DCA 봇 기능이 비활성화되어 있습니다. 새 봇 생성 및 가동이 차단됩니다.
            </AlertDescription>
          </Alert>
        )}

        {/* 엔진 중지 경고 */}
        {engine_stats && engine_stats.total_capacity === 0 && (
          <Alert variant="destructive" className="mb-6 bg-danger/10 border-danger/20 text-danger rounded-2xl shadow-sm animate-pulse">
            <AlertCircle className="h-5 w-5" />
            <AlertTitle className="font-black">매매 엔진(Sidekiq) 중지됨</AlertTitle>
            <AlertDescription className="font-bold text-xs opacity-80">
              현재 백엔드 트레이딩 엔진이 실행되지 않고 있습니다. 봇을 생성하거나 시작해도 실제 매매가 이루어지지 않습니다.
              백엔드 API 서버가 실행되고 있는지 확인해주세요.
            </AlertDescription>
          </Alert>
        )}

        {/* 서버 알림 표시 */}
        {showNotification && (flash?.notice || flash?.alert) && (
          <div className="fixed top-6 right-6 z-50 animate-in slide-in-from-right-full duration-500 max-w-sm w-full">
            <Alert className={`shadow-2xl border-none rounded-2xl ${flash.notice ? 'bg-success text-white' : 'bg-danger text-white'}`}>
              <div className="flex items-start gap-3">
                {flash.notice ? <CheckCircle2 className="w-5 h-5 mt-0.5" /> : <AlertCircle className="w-5 h-5 mt-0.5" />}
                <div className="flex-1">
                  <AlertTitle className="font-black text-sm">{flash.notice ? '성공' : '실패'}</AlertTitle>
                  <AlertDescription className="text-xs font-bold opacity-90">{flash.notice || flash.alert}</AlertDescription>
                </div>
                <button onClick={() => setShowNotification(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </Alert>
          </div>
        )}

        {hasDraft && (activeTab === 'recommend' || activeTab === 'simple' || activeTab === 'detail') && (
          <div className="animate-in slide-in-from-top duration-500 mb-6 font-sans">
            <div className="bg-warning/10 border border-warning/20 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-warning/20 rounded-xl">
                  <Settings2 className="w-5 h-5 text-warning" />
                </div>
                <div>
                  <p className="text-sm font-black text-foreground">이전에 작성 중인 설정이 있습니다.</p>
                  <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-tight">마지막으로 [설정 저장] 버튼을 누른 데이터입니다.</p>
                </div>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button onClick={loadDraft} className="flex-1 sm:flex-none bg-warning hover:opacity-90 text-white font-black text-xs px-6 rounded-xl shadow-md h-9">불러오기</Button>
                <Button onClick={clearDraft} variant="ghost" className="flex-1 sm:flex-none text-warning hover:bg-warning/10 font-bold text-xs h-9 px-4">삭제</Button>
              </div>
            </div>
          </div>
        )}

        {/* Inactive User Banner */}
        {!current_user.is_active && (activeTab === 'bots' || activeTab === 'recommend') && (
          <Alert className="mb-6 bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle className="font-bold">계정 승인 대기 중</AlertTitle>
            <AlertDescription>
              회원님의 계정이 아직 비활성 상태입니다. 관리자가 활성화한 후에 봇 가동 및 생성이 가능합니다.
            </AlertDescription>
          </Alert>
        )}



        {activeTab === 'bots' ? (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            {selectedBotId ? (
              <BotDetail
                bot={initial_bots.find(b => String(b.id) === String(selectedBotId))}
                onBack={() => {
                  setSelectedBotId(null);
                  router.reload({ only: ['initial_bots', 'engine_stats'], preserveUrl: true });
                }}
                is_testnet={is_testnet}
                disable_all_testnet={disable_all_testnet}
                enabled_exchanges={enabled_exchanges}
                testnet_min_notional={testnet_min_notional}
                onClone={handleCloneBot}
              />
            ) : (
              <ActiveBots
                bots={initial_bots}
                onBotClick={(id) => setSelectedBotId(id)}
                onAddBot={() => setActiveTab('recommend')}
                engine_stats={engine_stats}
                dca_enabled={dca_enabled}
                disable_all_testnet={disable_all_testnet}
                enabled_exchanges={enabled_exchanges}
                currentUser={{
                  email: current_user?.email,
                  logoutPath: current_user?.logout_path,
                  isActive: current_user?.is_active,
                  maxBots: current_user?.max_bots,
                  currentBotCount: current_user?.current_bot_count
                }}
              />
            )}
          </div>
        ) : activeTab === 'history' ? (
          <HistoryDashboard onSelectBot={(id) => setSelectedBotId(id)} disable_all_testnet={disable_all_testnet} />
        ) : activeTab === 'performance' ? (
          <DailyStats onSelectBot={(id) => setSelectedBotId(id)} disable_all_testnet={disable_all_testnet} />
        ) : activeTab === 'profile' ? (
          <ProfilePanel
            user={current_user}
            activeBotsCount={initial_bots.filter(b => b.is_active).length}
            disable_all_testnet={disable_all_testnet}
            enabled_exchanges={enabled_exchanges}
            maxBots={current_user.max_bots}
            autoScannerEnabled={auto_scanner_enabled}
            autoScannerMaxActiveSessions={auto_scanner_max_active_sessions}
            autoScannerExchange={auto_scanner_exchange}
            autoScannerTargetSymbols={auto_scanner_target_symbols}
          />
        ) : activeTab === 'admin' ? (
          <div className="flex flex-col items-center justify-center p-20 bg-card rounded-3xl border border-border/50">
            <Shield className="w-16 h-16 text-primary mb-4" />
            <h2 className="text-2xl font-bold mb-2">Admin Dashboard</h2>
            <p className="text-muted-foreground mb-8">관리자 전용 페이지로 이동하시겠습니까?</p>
            <a href="/admin" className="px-8 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition-all shadow-lg shadow-primary/20">
              관리자 페이지 열기
            </a>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-transparent animate-in fade-in slide-in-from-bottom-2 duration-300">
            <TabsList className="bg-card p-1 rounded-lg shadow-sm border border-border mb-8 flex w-full h-auto overflow-x-auto md:inline-flex md:w-auto">
              <TabsTrigger value="recommend" className="flex-1 rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 md:px-6 py-2 transition-all whitespace-nowrap">
                <LayoutDashboard className="w-4 h-4 mr-2 hidden sm:inline" />추천 전략
              </TabsTrigger>
              <TabsTrigger value="simple" className="flex-1 rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 md:px-6 py-2 transition-all whitespace-nowrap">
                <Zap className="w-4 h-4 mr-2 hidden sm:inline" />간편 설정
              </TabsTrigger>
              <TabsTrigger value="detail" className="flex-1 rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 md:px-6 py-2 transition-all whitespace-nowrap">
                <Settings2 className="w-4 h-4 mr-2 hidden sm:inline" />상세 설정
              </TabsTrigger>
            </TabsList>

            <TabsContent value="recommend" className="mt-0 ring-offset-background focus-visible:outline-none">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {STRATEGIES.map((strategy, index) => (
                  <StrategyCard key={index} {...strategy} onSelect={() => handleSelectStrategy(strategy)} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="simple" className="mt-0">
              <SimpleSettings
                selectedSymbol={selectedSymbol}
                setSelectedSymbol={setSelectedSymbol}
                botName={botName}
                setBotName={setBotName}
                baseQty={baseQty}
                setBaseQty={setBaseQty}
                positionType={positionType}
                setPositionType={setPositionType}
                leverage={leverage}
                setLeverage={setLeverage}
                entryCount={entryCount}
                setEntryCount={setEntryCount}
                entryMultiplier={entryMultiplier}
                setEntryMultiplier={setEntryMultiplier}
                entryInterval={entryInterval}
                setEntryInterval={setEntryInterval}
                takeProfit={takeProfit}
                setTakeProfit={setTakeProfit}
                repeatExecution={repeatExecution}
                setRepeatExecution={setRepeatExecution}
                repeatType={repeatType}
                setRepeatType={setRepeatType}
                stopLoss={stopLoss}
                setStopLoss={setStopLoss}
                stopLossValue={stopLossValue}
                setStopLossValue={setStopLossValue}
                stopBotOnSL={stopBotOnSL}
                setStopBotOnSL={setStopBotOnSL}
                feePct={feePct}
                setFeePct={setFeePct}
                limitFeePct={limitFeePct}
                setLimitFeePct={setLimitFeePct}
                balance={balance}
                setBalance={setBalance}
                useAllBalance={useAllBalance}
                setUseAllBalance={setUseAllBalance}
                candleInterval={candleInterval}
                setCandleInterval={setCandleInterval}
                entryType={entryType}
                setEntryType={handleEntryTypeChange}
                entryAmount={entryAmount}
                setEntryAmount={handleEntryAmountChange}
                predictedTotalOpenAmount={predictedTotalOpenAmount}
                isLive={isLive}
                setIsLive={handleIsLiveChange}
                principalAmount={principalAmount}
                setPrincipalAmount={handlePrincipalAmountChange}
                onStartBot={() => setShowConfirmation(true)}
                onSave={saveDraft}
                slStrategy={slStrategy}
                setSlStrategy={setSlStrategy}
                useLimitTp={useLimitTp}
                setUseLimitTp={setUseLimitTp}
                pullbackPct={pullbackPct}
                setPullbackPct={setPullbackPct}
                exchange={exchange}
                setExchange={handleExchangeChange}
                suspendedSides={suspendedSides}
                setSuspendedSides={setSuspendedSides}
                useTestnet={useTestnet}
                setUseTestnet={setUseTestnet}
                useProfitOffsetting={useProfitOffsetting}
                setUseProfitOffsetting={setUseProfitOffsetting}
                profitOffsetPct={profitOffsetPct}
                setProfitOffsetPct={setProfitOffsetPct}
                is_testnet={is_testnet}
                disable_all_testnet={disable_all_testnet}
                enabled_exchanges={enabled_exchanges}
                marginMode={marginMode}
                setMarginMode={setMarginMode}
                testnet_min_notional={testnet_min_notional}
                immediateReentry={immediateReentry}
                setImmediateReentry={setImmediateReentry}
                use1hTrendFilter={use1hTrendFilter}
                setUse1hTrendFilter={setUse1hTrendFilter}
                applyPreset={applyPreset}
                isRunning={false}
              />
            </TabsContent>

            <TabsContent value="detail" className="mt-0">
              <DetailedSettings
                selectedSymbol={selectedSymbol}
                setSelectedSymbol={setSelectedSymbol}
                botName={botName}
                setBotName={setBotName}
                baseQty={baseQty}
                setBaseQty={setBaseQty}
                positionType={positionType}
                setPositionType={setPositionType}
                leverage={leverage}
                setLeverage={setLeverage}
                takeProfit={takeProfit}
                setTakeProfit={setTakeProfit}
                immediateReentry={immediateReentry}
                setImmediateReentry={setImmediateReentry}
                dcaRounds={dcaRounds}
                addRound={addRound}
                removeRound={removeRound}
                updateRound={updateRound}
                repeatExecution={repeatExecution}
                setRepeatExecution={setRepeatExecution}
                repeatType={repeatType}
                setRepeatType={setRepeatType}
                stopLoss={stopLoss}
                setStopLoss={setStopLoss}
                stopLossValue={stopLossValue}
                setStopLossValue={setStopLossValue}
                stopBotOnSL={stopBotOnSL}
                setStopBotOnSL={setStopBotOnSL}
                useMaFilter={useMaFilter}
                setUseMaFilter={setUseMaFilter}
                useMa5Filter={useMa5Filter}
                setUseMa5Filter={setUseMa5Filter}
                useRsiEntry={useRsiEntry}
                setUseRsiEntry={setUseRsiEntry}
                useRsiScaling={useRsiScaling}
                setUseRsiScaling={setUseRsiScaling}
                useProfitOffsetting={useProfitOffsetting}
                setUseProfitOffsetting={setUseProfitOffsetting}
                profitOffsetPct={profitOffsetPct}
                setProfitOffsetPct={setProfitOffsetPct}
                useTsEntry={useTsEntry}
                setUseTsEntry={setUseTsEntry}
                useTsScaling={useTsScaling}
                setUseTsScaling={setUseTsScaling}
                maPeriod={maPeriod}
                setMaPeriod={setMaPeriod}
                rsiThreshold={rsiThreshold}
                setRsiThreshold={setRsiThreshold}
                use1hSidewaysFilter={use1hSidewaysFilter}
                setUse1hSidewaysFilter={setUse1hSidewaysFilter}
                use1hSidewaysScalingFilter={use1hSidewaysScalingFilter}
                setUse1hSidewaysScalingFilter={setUse1hSidewaysScalingFilter}
                adxThreshold={adxThreshold}
                setAdxThreshold={setAdxThreshold}
                use1hTrendFilter={use1hTrendFilter}
                setUse1hTrendFilter={setUse1hTrendFilter}
                use1hTrendSl={use1hTrendSl}
                setUse1hTrendSl={setUse1hTrendSl}
                use1hTrendBe={use1hTrendBe}
                setUse1hTrendBe={setUse1hTrendBe}
                isLive={isLive}
                setIsLive={handleIsLiveChange}
                onStartBot={() => setShowConfirmation(true)}
                onSave={saveDraft}
                feePct={feePct}
                setFeePct={setFeePct}
                limitFeePct={limitFeePct}
                setLimitFeePct={setLimitFeePct}
                balance={balance}
                setBalance={setBalance}
                useAllBalance={useAllBalance}
                setUseAllBalance={setUseAllBalance}
                candleInterval={candleInterval}
                setCandleInterval={setCandleInterval}
                entryType={entryType}
                setEntryType={handleEntryTypeChange}
                handleEntryTypeChange={handleEntryTypeChange}
                entryAmount={entryAmount}
                setEntryAmount={setEntryAmount}
                isAutoMargin={isAutoMargin}
                setIsAutoMargin={setIsAutoMargin}
                setDcaRounds={setDcaRounds}
                entryCount={entryCount}
                setEntryCount={setEntryCount}
                entryMultiplier={entryMultiplier}
                setEntryMultiplier={setEntryMultiplier}
                entryInterval={entryInterval}
                setEntryInterval={setEntryInterval}
                principalAmount={principalAmount}
                setPrincipalAmount={handlePrincipalAmountChange}
                handleEntryAmountChange={handleEntryAmountChange}
                isEntryFixed={isEntryFixed}
                setIsEntryFixed={setIsEntryFixed}
                marginMode={marginMode}
                setMarginMode={setMarginMode}
                slStrategy={slStrategy}
                setSlStrategy={setSlStrategy}
                tpThresholdStep={tpThresholdStep}
                setTpThresholdStep={setTpThresholdStep}
                tpThresholdPct={tpThresholdPct}
                setTpThresholdPct={setTpThresholdPct}
                useLimitTp={useLimitTp}
                setUseLimitTp={setUseLimitTp}
                pullbackPct={pullbackPct}
                setPullbackPct={setPullbackPct}
                exchange={exchange}
                setExchange={handleExchangeChange}
                suspendedSides={suspendedSides}
                setSuspendedSides={setSuspendedSides}
                useTestnet={useTestnet}
                setUseTestnet={setUseTestnet}
                is_testnet={is_testnet}
                disable_all_testnet={disable_all_testnet}
                enabled_exchanges={enabled_exchanges}
                testnet_min_notional={testnet_min_notional}
                useSuperMaFilter={useSuperMaFilter}
                setUseSuperMaFilter={setUseSuperMaFilter}
                useSuperMaBe={useSuperMaBe}
                setUseSuperMaBe={setUseSuperMaBe}
                useSuperMaScaling={useSuperMaScaling}
                setUseSuperMaScaling={setUseSuperMaScaling}
                useSuperMaSlowMode={useSuperMaSlowMode}
                setUseSuperMaSlowMode={setUseSuperMaSlowMode}
                superMaScoreThreshold={superMaScoreThreshold}
                setSuperMaScoreThreshold={setSuperMaScoreThreshold}
                useStochAlignmentEntry={useStochAlignmentEntry}
                setUseStochAlignmentEntry={setUseStochAlignmentEntry}
                applyPreset={applyPreset}
                telegramEnabled={telegramEnabled}
                setTelegramEnabled={setTelegramEnabled}
                telegramNotifyTypes={telegramNotifyTypes}
                setTelegramNotifyTypes={setTelegramNotifyTypes}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} current_user={current_user} />

      {current_user?.logout_path && (
        <div className="max-w-md mx-auto mt-4 pb-12 flex flex-col items-center gap-1 opacity-40 hover:opacity-100 transition-opacity">
          {(current_user.name || current_user.email) && (
            <a
              href="/dca#profile"
              className="text-[10px] font-bold text-muted-foreground/60 hover:text-primary transition-colors tracking-tight"
            >
              {current_user.name ? `${current_user.name} (${current_user.email})` : current_user.email}
            </a>
          )}
          <div className="flex items-center gap-3">
            {current_user.is_admin && (
              <a
                href="/admin"
                className="text-[10px] font-black text-muted-foreground/60 hover:text-primary transition-colors uppercase tracking-widest flex items-center gap-1"
              >
                <Shield className="w-3 h-3" /> /admin
              </a>
            )}
            {current_user.is_impersonated && (
              <a
                href="?user_id=clear"
                className="text-[11px] font-black text-amber-500 hover:text-amber-600 transition-colors uppercase tracking-widest"
              >
                돌아가기 (Exit Impersonation)
              </a>
            )}
            <button
              type="button"
              onClick={() => router.delete(current_user.logout_path)}
              className="text-[11px] font-black text-muted-foreground/80 hover:text-danger transition-colors uppercase tracking-widest"
            >
              Sign Out / 로그아웃
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
